import {
  AgentIcon,
  ClockIcon,
  DotFillIcon,
  StopIcon,
  SkipIcon,
  CheckCircleIcon,
  GitMergeIcon,
  GitPullRequestClosedIcon,
  ArchiveIcon,
  IterationsIcon,
  TerminalIcon,
  VscodeIcon,
  XCircleIcon,
  QuestionBubbleIcon,
} from '@primer/octicons-react'
import type {UITask} from '../types/task'
import type {ActiveTab} from '../types/filter-types'
import type {AgentInfo} from '@github-ui/agent-sessions-types/agent-identity'
import type {EnvironmentKind} from '@github-ui/agent-sessions-types/environment'
import {getAgentDisplayName} from '@github-ui/copilot-shared-ui/agents/agent-info-registry'

// --- Shared types ---

export type SortDirection = 'newest' | 'oldest'

export type OpenStatus =
  | 'in-progress'
  | 'queued'
  | 'idle'
  | 'needs-attention'
  | 'failed'
  | 'completed'
  | 'cancelled'
  | 'timed_out'
export type ClosedStatus = 'merged' | 'closed' | 'archived'
export type DetailedStatus = OpenStatus | ClosedStatus

export interface FilterOption {
  value: string
  label: string
  icon?: React.ComponentType<{className?: string}>
  avatarUrl?: string
  group?: 'agents' | 'custom'
  /** When set, selecting this option toggles all rawValues instead of just value. */
  rawValues?: string[]
  /** The agent type (cloud, cli, vscode, etc.) for agent filter options. */
  agentType?: string
}

export interface ParsedFilters {
  status: ActiveTab
  detailedStatuses: string[]
  creators: string[]
  excludedCreators: string[]
  agents: string[]
  types: string[]
  sort: SortDirection
  freeText: string
}

// --- Sidebar grouping (sq= URL param) ---

export const GROUP_BY_REPOSITORY = 'repository' as const
export const GROUP_BY_STATUS = 'status' as const
export const GROUP_BY_LAST_UPDATED = 'last-updated' as const
export const GROUP_BY_ENVIRONMENT = 'environment' as const

export type SidebarGroupBy =
  | typeof GROUP_BY_REPOSITORY
  | typeof GROUP_BY_STATUS
  | typeof GROUP_BY_LAST_UPDATED
  | typeof GROUP_BY_ENVIRONMENT

export function isSidebarGroupBy(value: unknown): value is SidebarGroupBy {
  return (
    value === GROUP_BY_REPOSITORY ||
    value === GROUP_BY_STATUS ||
    value === GROUP_BY_LAST_UPDATED ||
    value === GROUP_BY_ENVIRONMENT
  )
}

export function resolveSidebarGroupBy({
  urlGroupBy,
  storedGroupBy,
  filteringEnabled,
}: {
  urlGroupBy: unknown
  storedGroupBy: unknown
  filteringEnabled: boolean
}): SidebarGroupBy {
  if (!filteringEnabled) return GROUP_BY_REPOSITORY
  if (isSidebarGroupBy(urlGroupBy)) return urlGroupBy
  if (isSidebarGroupBy(storedGroupBy)) return storedGroupBy
  return GROUP_BY_LAST_UPDATED
}

export function isNonRepositoryGrouping(groupBy: SidebarGroupBy | undefined): boolean {
  return groupBy !== undefined && groupBy !== GROUP_BY_REPOSITORY
}

export interface SidebarParsedFilters extends ParsedFilters {
  groupBy?: SidebarGroupBy
  repo?: string
}

export const GROUP_QUALIFIER = 'group:'
export const REPO_QUALIFIER = 'repo:'

// --- Filter status constants (UI layer, used in URLs) ---

export const FILTER_STATUS_IN_PROGRESS = 'in-progress' as const
export const FILTER_STATUS_QUEUED = 'queued' as const
export const FILTER_STATUS_IDLE = 'idle' as const
export const FILTER_STATUS_NEEDS_ATTENTION = 'needs-attention' as const
export const FILTER_STATUS_FAILED = 'failed' as const
export const FILTER_STATUS_COMPLETED = 'completed' as const
export const FILTER_STATUS_CANCELLED = 'cancelled' as const
export const FILTER_STATUS_TIMED_OUT = 'timed_out' as const
export const FILTER_STATUS_MERGED = 'merged' as const
export const FILTER_STATUS_CLOSED = 'closed' as const
export const FILTER_STATUS_ARCHIVED = 'archived' as const

// Legacy snake_case filter values (for backward-compatible URL parsing)
export const LEGACY_FILTER_STATUS_IN_PROGRESS = 'in_progress' as const
export const LEGACY_FILTER_STATUS_NEEDS_ATTENTION = 'needs_attention' as const

// --- Constants ---

export const OPEN_STATUSES = new Set<string>([
  FILTER_STATUS_IN_PROGRESS,
  FILTER_STATUS_QUEUED,
  FILTER_STATUS_IDLE,
  FILTER_STATUS_NEEDS_ATTENTION,
  FILTER_STATUS_FAILED,
  FILTER_STATUS_COMPLETED,
  FILTER_STATUS_CANCELLED,
  FILTER_STATUS_TIMED_OUT,
])
export const CLOSED_STATUSES = new Set<string>([FILTER_STATUS_MERGED, FILTER_STATUS_CLOSED, FILTER_STATUS_ARCHIVED])
export const ALL_STATUSES = new Set<string>([...OPEN_STATUSES, ...CLOSED_STATUSES])

export const ME_ALIAS = '@me'
export const DEFAULT_FILTER_QUERY = 'is:open'

// Filter qualifier prefixes
export const IS_QUALIFIER = 'is:'
export const STATUS_QUALIFIER = 'status:'
export const AUTHOR_QUALIFIER = 'author:'
export const AGENT_QUALIFIER = 'agent:'
export const TYPE_QUALIFIER = 'type:'
export const SORT_QUALIFIER = 'sort:'

export const EXCLUDE_PREFIX = '-'

/** Valid agent type/group values for the Type filter dropdown. */
export const VALID_AGENT_TYPES = new Set(['cloud', 'cli', 'vscode'])

/**
 * Environment kinds that back "Cloud agents" sessions which have no cloud
 * agent ID of their own (e.g. sandbox and codespace sessions). The Type filter
 * resolves `cloud` to agent IDs via `include_agent_ids`; these environment-backed
 * sessions must be requested via `include_environment_kinds` too, otherwise they
 * are dropped when filtering by `Type: Cloud agents`.
 *
 * This is the single source of truth for that list: the global Copilot menu
 * re-exports it as `GLOBAL_OVERLAY_ENVIRONMENT_KINDS` so the full-page filter and
 * the menu can never diverge.
 */
export const CLOUD_TYPE_ENVIRONMENT_KINDS = [
  'managed-sandbox',
  'user-codespace',
] as const satisfies readonly EnvironmentKind[]

export const TYPE_OPTIONS: FilterOption[] = [
  {value: 'cloud', label: 'Cloud agents', icon: AgentIcon},
  {value: 'cli', label: 'CLI', icon: TerminalIcon},
  {value: 'vscode', label: 'VS Code', icon: VscodeIcon},
]

export function getTypeOptionsForAgents(agents: AgentInfo[]): FilterOption[] {
  const presentGroups = new Set<string>(agents.map(agent => agent.group ?? 'cloud'))
  return TYPE_OPTIONS.filter(option => presentGroups.has(option.value))
}

const OPEN_STATUS_OPTIONS: FilterOption[] = [
  {value: FILTER_STATUS_IN_PROGRESS, label: 'Working', icon: IterationsIcon},
  {value: FILTER_STATUS_QUEUED, label: 'Queued', icon: ClockIcon},
  {value: FILTER_STATUS_IDLE, label: 'Idle', icon: DotFillIcon},
  {value: FILTER_STATUS_NEEDS_ATTENTION, label: 'Needs attention', icon: QuestionBubbleIcon},
  {value: FILTER_STATUS_FAILED, label: 'Failed', icon: StopIcon},
  {value: FILTER_STATUS_COMPLETED, label: 'Done', icon: CheckCircleIcon},
  {value: FILTER_STATUS_CANCELLED, label: 'Cancelled', icon: SkipIcon},
  {value: FILTER_STATUS_TIMED_OUT, label: 'Timed out', icon: StopIcon},
]

const CLOSED_STATUS_OPTIONS: FilterOption[] = [
  {value: FILTER_STATUS_MERGED, label: 'Merged', icon: GitMergeIcon},
  {value: FILTER_STATUS_CLOSED, label: 'Closed', icon: GitPullRequestClosedIcon},
  {value: FILTER_STATUS_ARCHIVED, label: 'Archived', icon: ArchiveIcon},
]

export const ALL_STATUS_OPTIONS: FilterOption[] = [...OPEN_STATUS_OPTIONS, ...CLOSED_STATUS_OPTIONS]

// --- Backwards-compatible normalization ---

const LEGACY_STATUS_ALIASES: Record<string, string> = {
  [LEGACY_FILTER_STATUS_IN_PROGRESS]: FILTER_STATUS_IN_PROGRESS,
  [LEGACY_FILTER_STATUS_NEEDS_ATTENTION]: FILTER_STATUS_NEEDS_ATTENTION,
}

/**
 * Normalizes a detailed status value from legacy snake_case to kebab-case.
 * Accepts both formats for backwards compatibility with existing bookmarks/URLs.
 */
export function normalizeDetailedStatus(status: string): string {
  return LEGACY_STATUS_ALIASES[status] ?? status
}

/**
 * UI filter values (kebab-case) differ from API session state values (snake_case).
 */
export function mapDetailedStatusToSessionState(status: string): string {
  const normalized = normalizeDetailedStatus(status)
  if (normalized === FILTER_STATUS_NEEDS_ATTENTION) return 'waiting_for_user'
  if (normalized === FILTER_STATUS_IN_PROGRESS) return 'in_progress'
  return normalized
}

// Consolidated status options (behind copilot_mission_control_filters_followups FF)
// - "Idle" merged into "Done"
// - "Failed", "Timed out", "Cancelled" collapsed into "Unsuccessful"
// - "Queued" conditionally shown only when cloud agent type is selected or no type filter
const CONSOLIDATED_OPEN_STATUS_OPTIONS: FilterOption[] = [
  {value: FILTER_STATUS_IN_PROGRESS, label: 'Working', icon: IterationsIcon},
  {value: FILTER_STATUS_NEEDS_ATTENTION, label: 'Needs attention', icon: QuestionBubbleIcon},
  {
    value: FILTER_STATUS_COMPLETED,
    label: 'Done',
    icon: CheckCircleIcon,
    rawValues: [FILTER_STATUS_COMPLETED, FILTER_STATUS_IDLE],
  },
  {
    value: 'unsuccessful',
    label: 'Unsuccessful',
    icon: XCircleIcon,
    rawValues: [FILTER_STATUS_FAILED, FILTER_STATUS_TIMED_OUT, FILTER_STATUS_CANCELLED],
  },
  {value: FILTER_STATUS_QUEUED, label: 'Queued', icon: ClockIcon},
]

// --- Display slug helpers ---

/**
 * Converts a display name to a URL-friendly slug.
 * e.g. "Copilot Coding Agent" → "copilot-coding-agent", "VS Code" → "vs-code"
 */
export function toDisplaySlug(displayName: string): string {
  return displayName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
}

// --- Task helpers ---

export function getStatusOptionsForTab(activeTab: ActiveTab): FilterOption[] {
  if (activeTab === 'open') return OPEN_STATUS_OPTIONS
  if (activeTab === 'closed') return CLOSED_STATUS_OPTIONS
  return ALL_STATUS_OPTIONS
}

/**
 * Returns true if the selected agents allow the "Queued" status to be shown.
 * Queued is cloud-only, so it's hidden when ALL selected agents are explicitly
 * non-cloud built-in types (cli, vscode, etc.). Custom agents (no agentType)
 * are cloud-based and support queued.
 */
export function selectedAgentsAllowQueued(selectedAgents: string[], agentOptions: FilterOption[]): boolean {
  if (selectedAgents.length === 0) return true
  const allNonCloud = selectedAgents.every(agentValue => {
    const option = agentOptions.find(o => o.value === agentValue)
    return option?.agentType !== undefined && option.agentType !== 'cloud'
  })
  return !allNonCloud
}

/**
 * Returns consolidated status options for the given tab, with "Queued" shown
 * only when cloud agents could appear in the results. Queued is hidden when:
 * - The type filter excludes cloud, OR
 * - The agent filter only has non-cloud built-in agents selected (cli, vscode, etc.)
 * Custom agents (custom:*) are cloud-based and DO support queued.
 */
export function getConsolidatedStatusOptionsForTab(
  activeTab: ActiveTab,
  selectedTypes: string[],
  selectedAgents: string[] = [],
  agentOptions: FilterOption[] = [],
): FilterOption[] {
  const typeAllowsQueued = selectedTypes.length === 0 || selectedTypes.includes('cloud')
  const showQueued = typeAllowsQueued && selectedAgentsAllowQueued(selectedAgents, agentOptions)

  if (activeTab === 'closed') return CLOSED_STATUS_OPTIONS

  const openOptions = showQueued
    ? CONSOLIDATED_OPEN_STATUS_OPTIONS
    : CONSOLIDATED_OPEN_STATUS_OPTIONS.filter(opt => opt.value !== FILTER_STATUS_QUEUED)

  if (activeTab === 'open') return openOptions
  return [...openOptions, ...CLOSED_STATUS_OPTIONS]
}

// --- Filter query parsing/building ---

export function parseFilterQuery(query: string): ParsedFilters {
  const result: ParsedFilters = {
    status: 'all',
    detailedStatuses: [],
    creators: [],
    excludedCreators: [],
    agents: [],
    types: [],
    sort: 'newest',
    freeText: '',
  }
  const parts = query.split(/\s+/).filter(Boolean)
  const freeTextParts: string[] = []
  let hasStatusToken = false

  for (const part of parts) {
    if (part.startsWith(IS_QUALIFIER)) {
      hasStatusToken = true
      const value = part.slice(IS_QUALIFIER.length)
      if (value === 'closed') result.status = 'closed'
      else if (value === 'open') result.status = 'open'
    } else if (part.startsWith(STATUS_QUALIFIER)) {
      const value = normalizeDetailedStatus(part.slice(STATUS_QUALIFIER.length))
      if (ALL_STATUSES.has(value)) result.detailedStatuses.push(value)
    } else if (part.startsWith(`${EXCLUDE_PREFIX}${AUTHOR_QUALIFIER}`)) {
      const value = part.slice(EXCLUDE_PREFIX.length + AUTHOR_QUALIFIER.length)
      if (value) result.excludedCreators.push(value)
    } else if (part.startsWith(AUTHOR_QUALIFIER)) {
      const value = part.slice(AUTHOR_QUALIFIER.length)
      if (value) result.creators.push(value)
    } else if (part.startsWith(AGENT_QUALIFIER)) {
      const value = part.slice(AGENT_QUALIFIER.length)
      if (value) result.agents.push(value)
    } else if (part.startsWith(TYPE_QUALIFIER)) {
      const value = part.slice(TYPE_QUALIFIER.length)
      if (VALID_AGENT_TYPES.has(value)) result.types.push(value)
    } else if (part.startsWith(SORT_QUALIFIER)) {
      const value = part.slice(SORT_QUALIFIER.length)
      if (value === 'oldest') result.sort = 'oldest'
    } else {
      freeTextParts.push(part)
    }
  }

  if (!hasStatusToken) {
    result.status = 'all'
  }

  result.freeText = freeTextParts.join(' ')
  return result
}

export function buildFilterQuery(
  activeTab: ActiveTab,
  selectedCreators: string[],
  sortDirection: SortDirection,
  searchQuery: string,
  detailedStatuses: string[] = [],
  selectedAgents: string[] = [],
  currentUserLogin?: string,
  excludedCreators: string[] = [],
  selectedTypes: string[] = [],
): string {
  const parts: string[] = []
  if (activeTab !== 'all') parts.push(`${IS_QUALIFIER}${activeTab}`)
  for (const status of detailedStatuses) parts.push(`${STATUS_QUALIFIER}${normalizeDetailedStatus(status)}`)
  for (const creator of selectedCreators) {
    parts.push(`${AUTHOR_QUALIFIER}${currentUserLogin && creator === currentUserLogin ? ME_ALIAS : creator}`)
  }
  for (const creator of excludedCreators) {
    parts.push(
      `${EXCLUDE_PREFIX}${AUTHOR_QUALIFIER}${currentUserLogin && creator === currentUserLogin ? ME_ALIAS : creator}`,
    )
  }
  for (const agentType of selectedTypes) parts.push(`${TYPE_QUALIFIER}${agentType}`)
  for (const agent of selectedAgents) parts.push(`${AGENT_QUALIFIER}${agent}`)
  if (sortDirection === 'oldest') parts.push(`${SORT_QUALIFIER}oldest`)
  if (searchQuery) parts.push(searchQuery)
  return parts.join(' ')
}

// --- Sidebar grouping query parsing/building ---

export type SidebarFacetOptions = {includeFacets?: boolean; includeTypeFacet?: boolean}

export function parseSidebarFilterQuery(
  query: string,
  {includeFacets = true}: SidebarFacetOptions = {},
): SidebarParsedFilters {
  const parts = query.split(/\s+/).filter(Boolean)
  const remainingParts: string[] = []
  const detailedStatuses: string[] = []
  const types: string[] = []
  const agents: string[] = []
  let groupBy: SidebarGroupBy | undefined
  let repo: string | undefined

  for (const part of parts) {
    if (part.startsWith(GROUP_QUALIFIER)) {
      const value = part.slice(GROUP_QUALIFIER.length)
      if (isSidebarGroupBy(value)) groupBy = value
    } else if (part.startsWith(REPO_QUALIFIER)) {
      const value = part.slice(REPO_QUALIFIER.length)
      if (isSidebarRepo(value)) repo = value
      else remainingParts.push(part)
    } else if (includeFacets && part.startsWith(TYPE_QUALIFIER) && part !== TYPE_QUALIFIER) {
      types.push(part.slice(TYPE_QUALIFIER.length))
    } else if (includeFacets && part.startsWith(AGENT_QUALIFIER) && part !== AGENT_QUALIFIER) {
      agents.push(part.slice(AGENT_QUALIFIER.length))
    } else {
      remainingParts.push(part)
      if (part.startsWith(STATUS_QUALIFIER)) {
        const status = normalizeDetailedStatus(part.slice(STATUS_QUALIFIER.length))
        if (OPEN_STATUSES.has(status) && !detailedStatuses.includes(status)) detailedStatuses.push(status)
      }
    }
  }

  const parsed = parseFilterQuery(remainingParts.join(' '))
  // With facets off, type: and agent: keep the flat-list parsing that predates sidebar facets.
  if (!includeFacets) return {...parsed, detailedStatuses, groupBy, repo}
  return {
    ...parsed,
    detailedStatuses,
    types: dedupe(types),
    agents: dedupe(agents),
    groupBy,
    repo,
  }
}

function isSidebarRepo(value: string): boolean {
  return (
    /^[a-z\d_](?:[a-z\d_-]{0,37}[a-z\d_])?\/[a-z\d._-]{1,100}$/i.test(value) &&
    !value.endsWith('/.') &&
    !value.endsWith('/..')
  )
}

function dedupe<T>(values: readonly T[]): T[] {
  return [...new Set(values)]
}

export interface EffectiveSidebarFilters {
  statuses: string[]
  repo?: string
  // Facet values stay unvalidated so an unknown value matches nothing instead of disabling its facet.
  types: string[]
  agents: string[]
}

export function getEffectiveSidebarFilters(
  filters: SidebarParsedFilters,
  {includeFacets = false, includeTypeFacet = true}: SidebarFacetOptions = {},
): EffectiveSidebarFilters {
  const statuses = [...new Set(filters.detailedStatuses.map(normalizeDetailedStatus).filter(s => OPEN_STATUSES.has(s)))]
  const repo = filters.repo && isSidebarRepo(filters.repo) ? filters.repo : undefined
  if (!includeFacets) return {statuses, repo, types: [], agents: []}
  return {
    statuses,
    repo,
    types: includeTypeFacet ? dedupe(filters.types.filter(Boolean)) : [],
    agents: dedupe(filters.agents.filter(Boolean)),
  }
}

export function buildSidebarGroupFilterQuery(filters: SidebarParsedFilters, currentUserLogin?: string): string {
  const {statuses, repo} = getEffectiveSidebarFilters(filters, {includeFacets: true})
  const base = buildFilterQuery(
    filters.status,
    filters.creators,
    filters.sort,
    filters.freeText,
    statuses,
    dedupe(filters.agents),
    currentUserLogin,
    filters.excludedCreators,
    dedupe(filters.types),
  )
  const parts = [base]
  if (filters.groupBy) parts.push(`${GROUP_QUALIFIER}${filters.groupBy}`)
  if (repo) parts.push(`${REPO_QUALIFIER}${repo}`)
  return parts.filter(Boolean).join(' ')
}

export function hasActiveSidebarFilter(filters: SidebarParsedFilters, options: SidebarFacetOptions = {}): boolean {
  const {statuses, repo, types, agents} = getEffectiveSidebarFilters(filters, options)
  return statuses.length > 0 || repo !== undefined || types.length > 0 || agents.length > 0
}

export function sidebarFacetsNeedAgentMetadata(
  filters: SidebarParsedFilters,
  {includeTypeFacet = true}: SidebarFacetOptions = {},
): boolean {
  const {types, agents} = getEffectiveSidebarFilters(filters, {includeFacets: true, includeTypeFacet})
  return types.some(type => VALID_AGENT_TYPES.has(type)) || agents.some(agent => !agent.startsWith('custom:'))
}

export function clearSidebarFilters(
  filters: SidebarParsedFilters,
  {includeFacets = false, includeTypeFacet = true}: SidebarFacetOptions = {},
): SidebarParsedFilters {
  const cleared = {...filters, detailedStatuses: [], repo: undefined}
  return includeFacets ? {...cleared, types: includeTypeFacet ? [] : filters.types, agents: []} : cleared
}

export type SidebarFacetParams = {
  facetMode?: 'and'
  agentIds?: number[]
  customAgentName?: string
  includeTypeAgentIds?: number[]
  includeTypeEnvironmentKinds?: EnvironmentKind[]
}

export type SidebarFacetResolution =
  | {kind: 'none'}
  | {kind: 'resolving'}
  | {kind: 'unresolvable'; reason: 'metadata-error' | 'zero-matches'}
  | {kind: 'ready'; params: SidebarFacetParams}

export function resolveSidebarFacetParams(
  filters: SidebarParsedFilters,
  filterableAgents: AgentInfo[] | undefined,
  state: {agentsLoaded: boolean; agentsError: boolean},
  {includeTypeFacet = true}: SidebarFacetOptions = {},
): SidebarFacetResolution {
  const {types, agents} = getEffectiveSidebarFilters(filters, {includeFacets: true, includeTypeFacet})
  const hasTypeFacet = types.length > 0
  const hasAgentFacet = agents.length > 0
  const needsAgentMetadata = sidebarFacetsNeedAgentMetadata(filters, {includeTypeFacet})

  if (!hasTypeFacet && !hasAgentFacet) return {kind: 'none'}
  if (needsAgentMetadata && state.agentsError) return {kind: 'unresolvable', reason: 'metadata-error'}
  if (needsAgentMetadata && !state.agentsLoaded) return {kind: 'resolving'}

  const params: SidebarFacetParams = hasTypeFacet ? {facetMode: 'and'} : {}

  if (hasAgentFacet) {
    const {agentIds, customAgentNames} = resolveSidebarAgentFacet(agents, filterableAgents)
    if (agentIds.length === 0 && customAgentNames.length === 0) return {kind: 'unresolvable', reason: 'zero-matches'}
    if (agentIds.length > 0) params.agentIds = agentIds
    if (customAgentNames.length > 0) params.customAgentName = customAgentNames.join(',')
  }

  if (hasTypeFacet) {
    const {agentIds, environmentKinds} = resolveSidebarTypeFacet(types, filterableAgents)
    if (agentIds.length === 0 && environmentKinds.length === 0) return {kind: 'unresolvable', reason: 'zero-matches'}
    if (agentIds.length > 0) params.includeTypeAgentIds = agentIds
    if (environmentKinds.length > 0) params.includeTypeEnvironmentKinds = environmentKinds
  }

  return {kind: 'ready', params}
}

function resolveSidebarAgentFacet(
  agents: readonly string[],
  filterableAgents: AgentInfo[] | undefined,
): {agentIds: number[]; customAgentNames: string[]} {
  const customAgentNames = agents
    .filter(agent => agent.startsWith('custom:'))
    .map(agent => agent.slice('custom:'.length))
    .filter(agent => agent && !agent.includes(','))
  const displaySlugs = new Set(agents.filter(agent => !agent.startsWith('custom:')))
  const agentIds = (filterableAgents ?? []).flatMap(agent => {
    if (!displaySlugs.has(toDisplaySlug(getAgentDisplayName(agent))) || agent.id == null) return []
    const id = Number(agent.id)
    return Number.isFinite(id) ? [id] : []
  })
  return {agentIds: sortNumbers(dedupe(agentIds)), customAgentNames: dedupe(customAgentNames).sort()}
}

function resolveSidebarTypeFacet(
  types: readonly string[],
  filterableAgents: AgentInfo[] | undefined,
): {agentIds: number[]; environmentKinds: EnvironmentKind[]} {
  const selectedTypes = new Set(types.filter(type => VALID_AGENT_TYPES.has(type)))
  const agentIds = (filterableAgents ?? []).flatMap(agent => {
    if (!selectedTypes.has(agent.group ?? 'cloud') || agent.id == null) return []
    const id = Number(agent.id)
    return Number.isFinite(id) ? [id] : []
  })
  const environmentKinds = selectedTypes.has('cloud') ? [...CLOUD_TYPE_ENVIRONMENT_KINDS] : []
  return {agentIds: sortNumbers(dedupe(agentIds)), environmentKinds: environmentKinds.sort()}
}

function sortNumbers(values: number[]): number[] {
  return values.sort((a, b) => a - b)
}

// --- Task matching helpers ---

export function matchesSearch(task: UITask, query: string): boolean {
  const lower = query.toLowerCase()
  const title = task.pull?.title ?? ''
  if (title.toLowerCase().includes(lower)) return true
  if (task.name?.toLowerCase().includes(lower)) return true
  if (task.problemStatement?.toLowerCase().includes(lower)) return true
  if (task.creator_login?.toLowerCase().includes(lower)) return true
  return false
}

export function matchesDetailedStatus(task: UITask, status: string): boolean {
  status = normalizeDetailedStatus(status)
  if (status === FILTER_STATUS_NEEDS_ATTENTION) return task.latest_session_state === 'waiting_for_user'
  if (status === FILTER_STATUS_COMPLETED) {
    // A task is "completed" only if the session finished and the artifact isn't merged/closed.
    // Prefer artifact_state from the API; fall back to pull?.state only when artifact_state is absent.
    const effectiveState = task.artifact_state ?? task.pull?.state
    return task.latest_session_state === 'completed' && effectiveState !== 'merged' && effectiveState !== 'closed'
  }
  if (OPEN_STATUSES.has(status) && status !== FILTER_STATUS_NEEDS_ATTENTION && status !== FILTER_STATUS_COMPLETED)
    return task.latest_session_state === mapDetailedStatusToSessionState(status)
  if (status === FILTER_STATUS_MERGED) {
    const effectiveState = task.artifact_state ?? task.pull?.state
    return task.latest_session_state === 'completed' && effectiveState === 'merged'
  }
  if (status === FILTER_STATUS_CLOSED) {
    const effectiveState = task.artifact_state ?? task.pull?.state
    return task.latest_session_state === 'completed' && effectiveState === 'closed'
  }
  if (status === FILTER_STATUS_ARCHIVED) return !!task.archived_at
  return false
}
